import type { OperationType } from "../operation-types";
import { AGG_DELIMITER, type RiftConfig } from "../types";
import { aggregateToSql } from "./aggregate-to-sql";
import { escapeIdentifier } from "./escape-identifier";

/**
 * Resolves the column name as it appears in the inner query's output.
 *
 * When groups/aggregates are active the rift aliases multi-op aggregated
 * columns to `column::OP`. Single-op aggregates and CONCAT keep the original
 * name, as do auto-aggregated columns. The footer must reference these output
 * aliases, not the raw source column.
 */
const resolveInnerColumn = (
  column: string,
  riftConfig: RiftConfig | null | undefined,
): string => {
  if (!riftConfig) return column;

  const groups = riftConfig.groups ?? {};
  const aggregates = riftConfig.aggregates ?? {};

  const hasGroups = Object.values(groups).some(Boolean);
  const hasAggregates = Object.values(aggregates).some((ops) => ops?.length);
  if (!hasGroups && !hasAggregates) return column;

  if (groups[column]) return column;

  const ops = aggregates[column];
  if (ops?.length) {
    // Single-op aggregates and CONCAT keep the raw column name.
    if (ops.length === 1 || ops[0] === "CONCAT") return column;
    return `${column}${AGG_DELIMITER}${ops[0]}`;
  }

  // Auto-aggregated columns keep their original name
  return column;
};

/**
 * Wraps a pre-built SQL query with footer aggregate expressions.
 * The inner SQL should be the exact query that produces the table the user sees.
 *
 * When the inner query contains groups/aggregates, column names in the output
 * may differ from the source columns (e.g. `"gmv::SUM"`). Pass the
 * `riftConfig` so footers reference the correct output aliases.
 */
export const footersSql = (
  innerSql: string,
  footers: Record<string, OperationType>,
  riftConfig?: RiftConfig | null,
): string | null => {
  const hiddenSet = new Set(riftConfig?.hiddenColumns ?? []);

  const footerEntries = Object.entries(footers).filter(
    ([column]) => !hiddenSet.has(column),
  );
  if (footerEntries.length === 0) return null;

  const footerParts = footerEntries.map(([column, op]) => {
    const innerCol = resolveInnerColumn(column, riftConfig);
    const expr = aggregateToSql(innerCol, op);
    return `${expr} AS ${escapeIdentifier(column)}`;
  });

  return `SELECT ${footerParts.join(", ")} FROM (${innerSql})`;
};
